import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HardDrive, Clipboard, AlertTriangle, CheckCircle, Building, Wrench, Smartphone, Monitor, Usb, Camera } from 'lucide-react';
import { Users, FileText, Clock, MessageSquare, Activity, Keyboard, Wifi } from '../components/icons';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend
} from 'recharts';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getRoleLabel } from '../utils/roleLabels';
import DashboardOperativo from '../components/DashboardOperativo';
import AccesosRapidos from '../components/AccesosRapidos';
import DashboardSeguridad from '../components/DashboardSeguridad';

// ── Shared Constants & Components ──────────────────────────────────────────
const COLORS_PIE = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const COLORS_ESTADO = {
    'Pendiente': '#f59e0b', 'Asignado': '#3b82f6', 'En Proceso': '#f97316',
    'Esperando Usuario': '#a855f7', 'Resuelto': '#22c55e', 'Cerrado': '#6b7280', 'Cancelado': '#ef4444'
};
const catIcons = { 'Equipos': HardDrive, 'Móviles': Smartphone, 'Monitores': Monitor, 'Periféricos': Keyboard, 'Accesorios': Usb, 'Red': Wifi };

function Widget({ title, icon: Icon, children, className = '', span = 1, action }) {
    const spanClass = span === 2 ? 'lg:col-span-2' : span === 3 ? 'lg:col-span-3' : '';
    return (
        <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow ${spanClass} ${className}`}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4 text-blue-500" />}
                    {title}
                </h3>
                {action}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

function KpiCard({ label, value, icon: Icon, color = 'blue', onClick }) {
    const bg = {
        blue: 'bg-blue-50 dark:bg-blue-900/20', green: 'bg-emerald-50 dark:bg-emerald-900/20',
        yellow: 'bg-amber-50 dark:bg-amber-900/20', purple: 'bg-purple-50 dark:bg-purple-900/20',
        red: 'bg-red-50 dark:bg-red-900/20', indigo: 'bg-indigo-50 dark:bg-indigo-900/20',
        orange: 'bg-orange-50 dark:bg-orange-900/20',
    };
    const text = {
        blue: 'text-blue-600', green: 'text-emerald-600', yellow: 'text-amber-600',
        purple: 'text-purple-600', red: 'text-red-600', indigo: 'text-indigo-600',
        orange: 'text-orange-600',
    };
    return (
        <div
            onClick={onClick}
            className={`${bg[color] || bg.blue} rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105`}
        >
            <div className={`p-3 rounded-lg ${text[color] || text.blue} bg-white/60 dark:bg-black/10`}>
                <Icon className="w-6 h-6" />
            </div>
            <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            </div>
        </div>
    );
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm z-50">
            <p className="font-medium text-gray-900 dark:text-white mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color || p.fill }} className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color || p.fill }} />
                    {p.name}: <span className="font-semibold">{p.value}</span>
                </p>
            ))}
        </div>
    );
}

export default function Dashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [activityLogs, setActivityLogs] = useState([]);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const isAdminOrSoporte = user && (user.role === 'admin' || user.role === 'soporte');

        // Cargamos independientemente: si activity-log falla, el dashboard sigue funcionando
        api.get(`/dashboard?page=${currentPage}&pageSize=10`, { signal })
            .then(dashRes => {
                if (signal.aborted) return;
                setData(dashRes.data);
            })
            .catch(err => {
                if (signal.aborted) return;
                setError(err.message || 'Error al cargar el dashboard');
            })
            .finally(() => {
                if (!signal.aborted) setLoading(false);
            });

        if (isAdminOrSoporte) {
            api.get('/auth/activity-log', { signal })
                .then(logRes => {
                    if (signal.aborted) return;
                    const logs = Array.isArray(logRes.data?.logs) ? logRes.data.logs : [];
                    setActivityLogs(logs.filter(l => {
                        const a = (l.action || '').toLowerCase();
                        return !a.includes('login') && !a.includes('logout');
                    }));
                })
                .catch(() => { /* activity-log no crítico — el dashboard sigue */ });
        }

        return () => controller.abort();
    }, [currentPage, user]);

    if (loading) return <div className="p-6">Cargando dashboard...</div>;
    if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
    if (!data) return <div className="p-6 text-yellow-500">No hay datos disponibles</div>;

    const rawActivities = (Array.isArray(activityLogs) && activityLogs.length > 0)
        ? activityLogs
        : (Array.isArray(data?.ultimasActividades) ? data.ultimasActividades : []);

    const actividades = rawActivities.map(l => ({
        usuario: l.user?.username || l.usuario || l.username || 'Sistema',
        accion: l.action || l.accion || 'Actividad',
        descripcion: l.description || l.descripcion || '',
        fecha: l.timestamp || l.fecha || l.date || new Date().toISOString(),
        icono: l.icono || '🧩'
    }));

    // ── Recharts Data Prep ──────────────────────────────────────────
    const empresaData = (data.estadisticasEmpresas || []).map(e => ({
        name: e.empresa,
        value: e.cantidad
    }));

    const ticketEstadoData = [
        { name: 'Pendientes', value: data?.tickets?.pendientes || 0, fill: COLORS_ESTADO['Pendiente'] },
        { name: 'En Proceso', value: data?.tickets?.enProceso || 0, fill: COLORS_ESTADO['En Proceso'] },
        { name: 'Resueltos', value: data?.tickets?.resueltos || 0, fill: COLORS_ESTADO['Resuelto'] },
        { name: 'Cerrados', value: data?.tickets?.cerrados || 0, fill: COLORS_ESTADO['Cerrado'] }
    ].filter(x => x.value > 0);

    // ── Cards Data ──────────────────────────────────────────
    const mainStats = [
        { title: 'Activos Totales', value: data.estadisticas?.totalActivos ?? 0, icon: HardDrive, color: 'blue', onClick: () => navigate('/activos') },
        { title: 'Activos Asignados', value: data.estadisticas?.activosAsignados ?? 0, icon: Clipboard, color: 'green', onClick: () => { navigate('/activos'); sessionStorage.setItem('activos.filter.asignacion', 'asignados'); } },
        { title: 'Tickets Pendientes', value: data.tickets?.pendientes ?? 0, icon: AlertTriangle, color: 'orange', onClick: () => navigate('/tickets?estado=Pendiente') },
        { title: 'Usuarios Registrados', value: data.estadisticas?.totalUsuarios ?? 0, icon: Users, color: 'purple', onClick: () => navigate('/usuarios') },
    ];

    const secondaryStats = [
        { title: 'Activos Disponibles', value: data.estadisticas?.activosDisponibles ?? 0, icon: CheckCircle, color: 'green', onClick: () => { navigate('/activos'); sessionStorage.setItem('activos.filter.asignacion', 'sin_asignar'); } },
        { title: 'Actas Pendientes', value: data.actas?.pendientes ?? 0, icon: FileText, color: 'yellow', onClick: () => navigate('/gestion-actas?estado=Pendiente') },
        { title: 'Tickets En Proceso', value: data.tickets?.enProceso ?? 0, icon: Clock, color: 'blue', onClick: () => navigate('/tickets?estado=En%20Proceso') },
        { title: 'Activos Dados de Baja', value: data.estadisticas?.activosDadosDeBaja ?? 0, icon: Wrench, color: 'red', onClick: () => { navigate('/activos'); sessionStorage.setItem('activos.filter.estado', 'Baja'); } },
    ];

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto min-h-screen bg-slate-50 dark:bg-gray-900">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        ¡Bienvenido al Portal IT!
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        Panel de control para <b>{user?.role === 'admin' ? 'Administrador' : getRoleLabel(user?.role)}</b>
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                        <p className="text-sm text-gray-500 dark:text-gray-400">Última actualización</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-200">{new Date().toLocaleString()}</p>
                    </div>
                </div>
            </div>

            <AccesosRapidos />

            {(user?.role === 'admin' || user?.role === 'soporte') && <DashboardOperativo />}

            {(user?.role === 'admin' || user?.role === 'soporte') && <DashboardSeguridad />}

            {(user?.role === 'admin' || user?.role === 'soporte') && (
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white pt-2">Inventario y usuarios</h2>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {mainStats.map((s) => (
                    <KpiCard key={s.title} label={s.title} value={s.value} icon={s.icon} color={s.color} onClick={s.onClick} />
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {secondaryStats.map((s) => (
                    <KpiCard key={s.title} label={s.title} value={s.value} icon={s.icon} color={s.color} onClick={s.onClick} />
                ))}
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Widget title="Tickets por Estado" icon={MessageSquare}>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={ticketEstadoData}
                                    cx="50%" cy="50%"
                                    innerRadius={60} outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {ticketEstadoData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Widget>

                <Widget title="Distribución por Empresa" icon={Building}>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={empresaData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e5e7eb" />
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} fontSize={12} width={120} tick={{ fill: '#6b7280' }} />
                                <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                    {empresaData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS_PIE[index % COLORS_PIE.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Widget>
            </div>

            {/* Lists Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Actividades del Portal (solo admin/soporte) */}
                {(user?.role === 'admin' || user?.role === 'soporte') && (
                    <Widget title="Actividades del Portal" icon={Activity}>
                        <div className="space-y-3 max-h-96 sm:max-h-[28rem] overflow-y-auto pr-1">
                            {actividades && actividades.length > 0 ? (
                                actividades.map((actividad, i) => (
                                    <div key={i} className="flex items-start space-x-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                                            <span className="text-sm">{actividad.icono || '🔐'}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <p className="font-medium text-gray-900 dark:text-white text-sm">
                                                    {actividad.usuario}
                                                </p>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {new Date(actividad.fecha).toLocaleString()}
                                                </span>
                                            </div>
                                            <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                                                {actividad.accion}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                {actividad.descripcion}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8">
                                    <Activity className="mx-auto h-12 w-12 text-gray-400" />
                                    <p className="text-gray-500 dark:text-gray-400 mt-2">No hay actividades recientes</p>
                                </div>
                            )}
                        </div>
                    </Widget>
                )}

                {/* Últimos Tickets */}
                <Widget title="Últimos Tickets" icon={MessageSquare}>
                    <div className="space-y-3 max-h-96 sm:max-h-[28rem] overflow-y-auto pr-1">
                        {data.ultimosTickets && data.ultimosTickets.length > 0 ? (
                            data.ultimosTickets.map((ticket, i) => (
                                <div
                                    key={i}
                                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900 dark:text-white">
                                            #{ticket.id} - {ticket.descripcion}
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {ticket.solicitante} • {ticket.prioridad}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <span className={`px-2 py-1 text-xs rounded-full border border-gray-200 dark:border-gray-600`} style={{ color: COLORS_ESTADO[ticket.estado] || '#6b7280' }}>
                                            {ticket.estado}
                                        </span>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {new Date(ticket.fecha).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                No hay tickets recientes
                            </p>
                        )}
                    </div>
                </Widget>
            </div>

            {user?.role === 'soporte' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tickets asignados al soporte */}
                    <Widget title="Mis Tickets Asignados" icon={MessageSquare}>
                        <div className="space-y-3 max-h-96 sm:max-h-[28rem] overflow-y-auto pr-1">
                            {data.ticketsAsignados && data.ticketsAsignados.length > 0 ? (
                                data.ticketsAsignados.map((ticket, i) => (
                                    <div key={i} onClick={() => navigate(`/tickets/${ticket.id}`)} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                #{ticket.id} - {ticket.descripcion}
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                Prioridad: {ticket.prioridad}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className={`px-2 py-1 text-xs rounded-full border border-gray-200 dark:border-gray-600`} style={{ color: COLORS_ESTADO[ticket.estado] || '#6b7280' }}>
                                                {ticket.estado}
                                            </span>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {new Date(ticket.fecha).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No tienes tickets asignados
                                </p>
                            )}
                        </div>
                    </Widget>

                    {/* Activos con problemas */}
                    <Widget title="Activos con Problemas" icon={AlertTriangle}>
                        <div className="space-y-3 max-h-96 sm:max-h-[28rem] overflow-y-auto pr-1">
                            {data.activosConProblemas && data.activosConProblemas.length > 0 ? (
                                data.activosConProblemas.map((activo, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {activo.codigo}
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {activo.categoria}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                                {activo.ticketsAbiertos} tickets
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                    No hay activos con problemas reportados
                                </p>
                            )}
                        </div>
                    </Widget>
                </div>
            )}

            {/* Personas con alertas */}
            <Widget title="Personas con Alertas (Revisión sugerida)" icon={AlertTriangle}>
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-2 sm:gap-3">
                    {data.personasConAlertas && data.personasConAlertas.length > 0 ? (
                        data.personasConAlertas.map((persona, i) => (
                            <div
                                key={i}
                                onClick={() => navigate(`/usuarios/${persona.id}`)}
                                className="group cursor-pointer p-2 sm:p-3 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border border-red-200 dark:border-red-700 rounded-lg hover:shadow-md hover:scale-102 transition-all duration-200 hover:border-red-300 dark:hover:border-red-600 min-h-[80px] sm:min-h-[90px]"
                            >
                                <div className="flex items-center justify-between mb-1 sm:mb-2">
                                    <div className="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1">
                                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                                            {persona.nombre.split(' ').map(n => n[0]).join('').toUpperCase()}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm truncate group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                                                {persona.nombre}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                                {persona.empresa} • {persona.departamento}
                                            </p>
                                        </div>
                                    </div>
                                    <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 opacity-70 flex-shrink-0" />
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-1 flex-wrap">
                                        <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-semibold bg-red-500 text-white">
                                            {persona.alertas.totalAlertas}
                                        </span>
                                        {persona.alertas.onboardingIncompleto && (
                                            <span className="inline-flex items-center px-1 sm:px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                                Onb
                                            </span>
                                        )}
                                        {persona.alertas.actasPendientes && (
                                            <span className="inline-flex items-center px-1 sm:px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                                Actas
                                            </span>
                                        )}
                                        {persona.alertas.ticketsPendientes && (
                                            <span className="inline-flex items-center px-1 sm:px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                Tck
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full flex flex-col items-center justify-center py-12">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                                <AlertTriangle className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 dark:text-gray-400 text-center">
                                No hay personas con alertas activas
                            </p>
                            <p className="text-sm text-gray-400 dark:text-gray-500 text-center mt-1">
                                Todos los usuarios están al día
                            </p>
                        </div>
                    )}
                </div>

                {/* Controles de paginación */}
                {data?.personasConAlertasPagination && data.personasConAlertasPagination.totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex flex-col sm:flex-row items-center sm:space-x-2 text-center sm:text-left">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Página {data.personasConAlertasPagination.page} de {data.personasConAlertasPagination.totalPages}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                ({data.personasConAlertasPagination.total} usuarios con alertas)
                            </span>
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Anterior
                            </button>

                            <div className="flex flex-wrap items-center justify-center gap-1">
                                {Array.from({ length: Math.min(5, data.personasConAlertasPagination.totalPages) }, (_, i) => {
                                    const pageNum = Math.max(1, Math.min(
                                        data.personasConAlertasPagination.totalPages - 4,
                                        currentPage - 2
                                    )) + i;

                                    if (pageNum > data.personasConAlertasPagination.totalPages) return null;

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`px-3 py-1 text-sm rounded-md transition-colors ${currentPage === pageNum
                                                ? 'bg-red-500 text-white'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(data.personasConAlertasPagination.totalPages, prev + 1))}
                                disabled={currentPage === data.personasConAlertasPagination.totalPages}
                                className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
            </Widget>

            {/* Acciones rápidas (Solo Admin o Soporte) */}
            {(user?.role === 'admin' || user?.role === 'soporte') && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                        <Activity className="w-5 h-5 mr-2" />
                        Acciones Rápidas
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <Link
                            to="/activos"
                            className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                            <HardDrive className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400" />
                            <span className="font-medium text-gray-900 dark:text-white">Gestionar Activos</span>
                        </Link>
                        <Link
                            to="/tickets"
                            className="flex items-center p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                        >
                            <MessageSquare className="w-5 h-5 mr-3 text-orange-600 dark:text-orange-400" />
                            <span className="font-medium text-gray-900 dark:text-white">Ver Tickets</span>
                        </Link>
                        <Link
                            to="/usuarios"
                            className="flex items-center p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                        >
                            <Users className="w-5 h-5 mr-3 text-purple-600 dark:text-purple-400" />
                            <span className="font-medium text-gray-900 dark:text-white">Gestionar Usuarios</span>
                        </Link>
                        <Link
                            to="/gestion-actas"
                            className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                        >
                            <FileText className="w-5 h-5 mr-3 text-green-600 dark:text-green-400" />
                            <span className="font-medium text-gray-900 dark:text-white">Gestionar Actas</span>
                        </Link>
                        <Link
                            to="/evidencias-seguridad"
                            className="flex items-center p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors"
                        >
                            <Camera className="w-5 h-5 mr-3 text-teal-600 dark:text-teal-400" />
                            <span className="font-medium text-gray-900 dark:text-white">Evidencias Seguridad</span>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}
